import { useState } from "react";
import { PageTemplate } from "@/views/PageTemplate";
import { TokenOption } from "@/components/ui/types";
import stakeIcon from "@/assets/icons/stake.svg?url";
import piqueTokenImage from "@/assets/tokens/icon_i-onlight.svg?url";

// Let's create a placeholder PIQUE token image for now
// In a real implementation, you would import the actual image
// import piqueTokenImage from "@/assets/tokens/pique.svg?url";

export function StakePage() {
  // Define PIQUE token
  const piqueToken: TokenOption = {
    id: "pique",
    name: "PIQUE",
    icon: <img src={piqueTokenImage} alt='PIQUE' className='w-6 h-6 rounded-full' />,
  };

  // State declarations
  const [amount, setAmount] = useState("400");
  const [lockupDuration, setLockupDuration] = useState(8); // In months, 8 months based on the image
  const [variableAPR] = useState("~6.76%");
  const [endingDate] = useState("Nov 26, 2025");

  const handleStake = () => {
    console.log("Stake executed:", { amount, lockupDuration, variableAPR, endingDate });
  };

  // Calculate slider position (0 to 100)
  const sliderPosition = (lockupDuration / 12) * 100; // 12 months = 1 year

  return (
    <PageTemplate title='STAKING' subtitle='Lock tokens to earn rewards and support the network.'>
      <div className='max-w-[792px] mx-auto'>
        <div className='rounded-xl border border-gray-200 overflow-hidden'>
          <div className='bg-white p-6 md:p-8'>
            {/* Amount to stake input */}
            <div className='mb-6'>
              <p className='text-gray-700 mb-2 text-left text-xs'>Amount to stake...</p>
              <div className='border border-gray-200 rounded-lg overflow-hidden'>
                <div className='flex items-center'>
                  <div className='flex items-center gap-2 pl-4'>
                    {piqueToken.icon}
                    <span className='text-black'>{piqueToken.name}</span>
                  </div>
                  <input
                    type='text'
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder='0'
                    className='flex-1 w-full p-4 text-right text-black text-xl font-regular focus:outline-none'
                  />
                </div>
                <div className='text-left border-t border-gray-200 px-4 py-3 bg-gray-50'>
                  <p className='text-gray-600 text-sm'>Current Balance: 642.25 PIQUE</p>
                </div>
              </div>
            </div>

            {/* Duration, APR, and End Date */}
            <div className='grid grid-cols-10 gap-6 mt-6'>
              {/* Duration Slider - now takes up exactly 60% of the space */}
              <div className='col-span-10 md:col-span-6'>
                <p className='text-gray-700 mb-2 text-left text-xs'>Duration of lockup...</p>
                <div className='mt-2'>
                  <div className='relative'>
                    {/* Custom styled slider that matches the image */}
                    <div className='relative pt-2 pb-6'>
                      {/* Track background (gray) */}
                      <div className='absolute h-1.5 w-full bg-gray-200 rounded-full'></div>

                      {/* Track filled part (black) - width based on slider position */}
                      <div className='absolute h-1.5 bg-black rounded-full' style={{ width: `${sliderPosition.toString()}%` }}></div>

                      {/* Slider thumb (white circle with border) */}
                      <div
                        className='absolute w-5 h-5 bg-white border-2 border-black rounded-full -mt-1.5 transform -translate-x-1/2'
                        style={{ left: `${sliderPosition.toString()}%` }}
                      ></div>

                      {/* Hidden actual input for functionality */}
                      <input
                        type='range'
                        min='0'
                        max='100'
                        value={sliderPosition}
                        onChange={(e) => {
                          const position = parseInt(e.target.value);
                          const months = Math.round((position / 100) * 12); // Convert to months
                          setLockupDuration(months);
                        }}
                        className='absolute inset-0 w-full h-4 opacity-0 cursor-pointer'
                      />

                      {/* Duration markers */}
                      <div className='flex justify-between text-xs text-gray-600 mt-5'>
                        <span>0</span>
                        <span>3m</span>
                        <span>6m</span>
                        <span>9m</span>
                        <span>1y</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Variable APR and Ending Date - sharing the remaining 40% of space */}
              <div className='col-span-5 md:col-span-2'>
                <p className='text-gray-700 mb-2 text-left text-xs'>Variable APR</p>
                <div className='border border-gray-200 rounded-lg p-4 h-[60px] flex items-center justify-center'>
                  <span className='text-sm font-medium text-gray-900'>{variableAPR}</span>
                </div>
              </div>

              {/* Ending Date */}
              <div className='col-span-5 md:col-span-2'>
                <p className='text-gray-700 mb-2 text-left text-xs'>Ending on...</p>
                <div className='border border-gray-200 rounded-lg p-4 h-[60px] flex items-center justify-center'>
                  <span className='text-sm font-medium text-gray-900'>{endingDate}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Transaction Review */}
          <div className='bg-gray-50 p-6 md:p-8'>
            <h3 className='font-medium text-gray-800 mb-4 text-left text-xs'>Transaction Review</h3>
            <div className='space-y-1 text-sm'>
              <div className='flex items-start'>
                <svg className='w-5 h-5 mr-3 text-gray-600 flex-shrink-0 mt-0.5' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                  <path d='M5 12L10 17L19 8' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                </svg>
                <span className='text-gray-700 text-left'>
                  You are staking {amount}.00 PIQUE tokens for {lockupDuration} months, ending on {endingDate}.
                </span>
              </div>
              <div className='flex items-start'>
                <svg className='w-5 h-5 mr-3 text-gray-600 flex-shrink-0 mt-0.5' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                  <path d='M5 12L10 17L19 8' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                </svg>
                <span className='text-gray-700 text-left'>You will be earning a variable APY of {variableAPR}.</span>
              </div>
            </div>
          </div>
        </div>

        {/* Stake Button */}
        <div className='flex justify-center mt-6'>
          <button
            onClick={handleStake}
            className='flex items-center justify-center gap-2 bg-black text-white py-3 px-6 rounded-lg font-medium hover:bg-gray-800 transition-colors'
          >
            Stake & Start Earning
            <img src={stakeIcon} alt='Stake' className='w-4 h-4' />
          </button>
        </div>
      </div>
    </PageTemplate>
  );
}
